import React, { useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Swal from 'sweetalert2';

import PublicLayout from './components/layouts/public.layout';
import HomePage from './pages/home.page';
import AboutPage from './pages/about.page';
import ProtectedLayout from './components/layouts/protected.layout';
import MyAccountDashboardPage from './pages/my-account/dashboard.page';
import MyAccountProfilePage from './pages/my-account/profile.page';
import MyAccountChangePasswordPage from './pages/my-account/change-password.page';
import MyAccountAuctionsPage from './pages/my-account/auctions.page';
import MyAccountBidsPage from './pages/my-account/bids.page';
import { AppDispatch, RootState } from './store';
import { getConfigurations, getLoggedInuserProfile } from './apis';
import { setSystemConfig, showSnackbar } from './store/slices/common.slice';
import { getApiErrorMessage, isAdminUser } from './utilities/helper';
import { APIResponse, User } from './utilities/types';
import { updateLoggedUser } from './store/slices/auth.slice';
import MyAccountAuctionDetailPage from './pages/my-account/auction-detail.page';
import AuctionsPage from './pages/auctions.page';

// --- Admin pages & layouts (make sure these files exist in your project) ---
import AdminLayout from './components/layouts/admin.layout';
import AdminLoginPage from './pages/admin/login.page';
import AdminResetPasswordPage from './pages/admin/reset-password.page';
import AdminDashboardPage from './pages/admin/dashboard.page';
import AdminUsersPage from './pages/admin/users.page';
import AdminAuctionsPage from './pages/admin/auctions.page';
import AdminPublicLayout from './components/layouts/admin-public.layout';
import AdminItemsPage from './pages/admin/items.page';
import AdminSubItemsPage from './pages/admin/sub-items.page';
import AdminSellersPage from './pages/admin/sellers.page';
import AdminBuyersPage from './pages/admin/buyers.page';
import AdminProfilePage from './pages/admin/profile.page';
import ItemsPage from './pages/items.page';
import HowItWorkPage from './pages/how-it-work.page';
import SteelInfoPage from './pages/steel-info.page';
import TermAndConditionPage from './pages/term-condition.page';
import PrivacyPolicyPage from './pages/privacy-policy.page';
import AuctionDetailPage from './pages/auction-detail.page';

// --- helper guard components (in-file) ---

/**
 * When admin is logged in and tries to access public (frontend) pages,
 * redirect them to admin dashboard.
 */
// const FrontendPublicGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
//   // We need hooks here so create a component wrapper
//   const { user, token, isAuthenticated } = useSelector((state: RootState) => state.auth);
//   // If logged in and admin -> redirect to admin dashboard
//   if (token && isAuthenticated && isAdminUser(user)) {
//     return <Navigate to="/admin/dashboard" replace />;
//   }
//   // otherwise allow
//   return children;
// };

/**
 * Admin public guard for routes like /admin/login and /admin/reset-password
 * - If user is logged-in admin -> redirect to /admin/dashboard
 * - If user is logged-in non-admin -> redirect to my-account profile (or /)
 */
const AdminPublicGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, token, isAuthenticated } = useSelector((state: RootState) => state.auth);
  if (token && isAuthenticated) {
    if (isAdminUser(user)) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    // logged in but not admin -> redirect to profile (they shouldn't access admin)
    return <Navigate to="/my-account/profile" replace />;
  }
  return children;
};

/**
 * AdminProtectedGuard ensures route is accessible only to logged-in admin users.
 * - If not logged or token missing -> redirect to /admin/login
 * - If logged but not admin -> redirect to my-account profile (or /)
 */
const AdminProtectedGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, token, isAuthenticated } = useSelector((state: RootState) => state.auth);

  if (!token || !isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!isAdminUser(user)) {
    // if they are logged in but not admin, send them to their profile or home
    return <Navigate to="/my-account/profile" replace />;
  }

  return children;
};

// --- End helper components ---

const App: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { token, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const getUserProfile = useCallback(async () => {
    try {
      if (!token || !isAuthenticated) {
        return;
      }
      const response = (await getLoggedInuserProfile()) as APIResponse<User>;
      if (response.status) {
        dispatch(updateLoggedUser(response.data));
      } else {
        throw new Error('Error in fetching logged in user detail');
      }
    } catch (err) {
      dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthenticated]);

  const getConfig = useCallback(() => {
    getConfigurations()
      .then((response: any) => {
        if (response.status) {
          dispatch(setSystemConfig(response.data));
        }
      })
      .catch((err) => {
        console.error('========= Get Configuration error', err);
      });
  }, [dispatch]);

  useEffect(() => {
    getUserProfile();
  }, [getUserProfile]);

  useEffect(() => {
    getConfig();
  }, [getConfig]);

  useEffect(() => {
    // Set SweetAlert defaults globally
    Swal.mixin({
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: theme.palette.error.main,
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    });
  }, [theme]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <Box>
          <Routes>
            {/* ADMIN PUBLIC routes */}
            <Route path="admin" element={<AdminPublicLayout />}>
              <Route
                path="login"
                element={
                  <AdminPublicGuard>
                    <AdminLoginPage />
                  </AdminPublicGuard>
                }
              />
              <Route
                path="reset-password"
                element={
                  <AdminPublicGuard>
                    <AdminResetPasswordPage />
                  </AdminPublicGuard>
                }
              />
            </Route>

            {/* ADMIN PROTECTED routes (all other /admin/*) */}
            <Route
              path="/admin/*"
              element={
                <AdminProtectedGuard>
                  <AdminLayout />
                </AdminProtectedGuard>
              }
            >
              {/* nested admin routes inside AdminLayout */}
              <Route path="" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="my-account" element={<AdminProfilePage />} />
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="auctions" element={<AdminAuctionsPage />} />
              <Route path="sellers" element={<AdminSellersPage />} />
              <Route path="buyers" element={<AdminBuyersPage />} />
              <Route path="catalog" element={<AdminItemsPage />} />
              <Route path="catalog/items" element={<AdminItemsPage />} />
              <Route path="catalog/sub-items" element={<AdminSubItemsPage />} />
              {/* add more admin routes as required */}
            </Route>

            {/* PUBLIC / FRONTEND pages */}
            <Route path="/" element={<PublicLayout />}>
              {/* Wrap frontend public pages with FrontendPublicGuard to redirect admin users to admin dashboard */}
              <Route path="/" element={<HomePage />} />
              <Route path="/index.html" element={<HomePage />} />
              <Route path="/about.html" element={<AboutPage />} />
              <Route path="/scrape-items.html" element={<ItemsPage />} />
              <Route path="/auctions.html" element={<AuctionsPage />} />
              <Route path="/how-it-works.html" element={<HowItWorkPage />} />
              <Route path="/steel-information.html" element={<SteelInfoPage />} />
              <Route path="/terms-and-conditions.html" element={<TermAndConditionPage />} />
              <Route path="/privacy-policy.html" element={<PrivacyPolicyPage />} />
              <Route path="auction-detail.html" element={<AuctionDetailPage />} />

              {/* My-account protected area (existing behavior) */}
              <Route path="my-account" element={<ProtectedLayout />}>
                <Route index element={<MyAccountDashboardPage />} />
                <Route path="profile" element={<MyAccountProfilePage />} />
                <Route path="change-password" element={<MyAccountChangePasswordPage />} />
                <Route path="auctions" element={<MyAccountAuctionsPage />} />
                <Route path="bids" element={<MyAccountBidsPage />} />
                <Route path="auction-detail/:auction_id" element={<MyAccountAuctionDetailPage />} />
              </Route>

              {/* fallback: any unknown route -> home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Box>
      </Router>
    </LocalizationProvider>
  );
};

export default App;
